var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// backendClient.ts – Central API handler for backend communication
import { authenticateWithSRP } from './services/cognitoSRPService';
export const BACKEND_URL = process.env.BACKEND_URL || 'http://localhost:3001';
// ==============================
// TOKEN MONITORING
// ==============================
let tokenCheckInterval = null;
/**
 * Start monitoring token expiration
 */
export function startTokenMonitoring() {
    // Clear existing interval if any
    if (tokenCheckInterval) {
        clearInterval(tokenCheckInterval);
    }
    // Check token every 2 minutes
    tokenCheckInterval = setInterval(() => __awaiter(this, void 0, void 0, function* () {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            console.log('🔒 No token found, stopping monitoring');
            stopTokenMonitoring();
            return;
        }
        try {
            // Test token with a simple request
            const response = yield fetch(`${BACKEND_URL}/auth/me`, {
                method: 'GET',
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                console.warn('🔒 Token validation failed, attempting refresh');
                const refreshSuccess = yield refreshAccessToken();
                if (!refreshSuccess) {
                    console.warn('🔒 Token refresh failed, logging out');
                    forceLogout();
                    stopTokenMonitoring();
                }
            }
        }
        catch (error) {
            console.error('🔒 Token validation error:', error);
            // Don't logout on network errors, just log
        }
    }), 2 * 60 * 1000); // 2 minutes
    console.log('🔒 Token monitoring started');
}
/**
 * Stop monitoring token expiration
 */
export function stopTokenMonitoring() {
    if (tokenCheckInterval) {
        clearInterval(tokenCheckInterval);
        tokenCheckInterval = null;
        console.log('🔒 Token monitoring stopped');
    }
}
// ==============================
// SESSION FUNCTIONS
// ==============================
/**
 * Get the active session ID for the current user
 */
export function getActiveSession() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Define the request function for potential retry
            const makeRequest = () => __awaiter(this, void 0, void 0, function* () {
                return fetch(`${BACKEND_URL}/frontend/active-session`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
            });
            // Make initial request
            let response = yield makeRequest();
            // Handle auth errors with automatic retry
            if (!response.ok) {
                response = yield handleAuthError(response, makeRequest);
            }
            if (!response.ok) {
                const errorData = yield response.json();
                return {
                    success: false,
                    error: errorData.error || 'Failed to get active session'
                };
            }
            const data = yield response.json();
            return {
                success: true,
                sessionID: data.sessionID
            };
        }
        catch (error) {
            console.error('Error getting active session:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    });
}
// ==============================
// AUTHENTICATION FUNCTIONS
// ==============================
/**
 * Find username by email address (via backend)
 * @param email - Email address to search for
 * @returns Promise with username if found, or error
 */
export function findUsernameByEmail(email) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(`${BACKEND_URL}/auth/find-username-by-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });
            const data = yield response.json();
            return data;
        }
        catch (error) {
            console.error('❌ [BackendClient] Error finding username by email:', error);
            return {
                success: false,
                error: error.message || 'Failed to find username by email'
            };
        }
    });
}
/**
 * Check if a string is a valid email address
 */
function isValidEmail(str) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(str);
}
/**
 * Login user with SRP (Secure Remote Password) protocol
 *
 * This function is used for NORMAL logins (users with permanent passwords).
 * It uses USER_SRP_AUTH which:
 * - Never sends the password in clear text
 * - Communicates directly with Cognito (does not go through backend)
 * - Is the most secure method for client-side authentication
 *
 * Supports both username and email login:
 * - If email is provided and SRP fails with UserNotFoundException,
 *   the backend is called to find the username, then SRP is retried with the username.
 *
 * For temporary passwords (first-time login), use loginUserWithTemporaryPassword() instead.
 *
 * @param usernameOrEmail - User's username or email address
 * @param password - User's permanent password
 * @returns Promise with authentication result containing tokens or error
 */
export function loginUser(usernameOrEmail, password) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const identifier = usernameOrEmail.trim();
            const isEmail = isValidEmail(identifier);
            let usernameToUse = identifier;
            // If identifier is an email, find the username first to avoid unnecessary SRP errors
            if (isEmail) {
                console.log('🔍 [BackendClient] Email detected, finding username...');
                const usernameResult = yield findUsernameByEmail(identifier);
                if (usernameResult.success && usernameResult.username) {
                    console.log('✅ [BackendClient] Found username for email, using it for authentication');
                    usernameToUse = usernameResult.username;
                }
                else {
                    // Email not found, but still try with email in case User Pool allows email as username
                    console.log('⚠️ [BackendClient] Username not found for email, trying with email directly');
                    usernameToUse = identifier;
                }
            }
            // Use SRP authentication with username (or email if username not found)
            const result = yield authenticateWithSRP(usernameToUse, password);
            if (result.success && result.tokens) {
                // Store username (not email) for refresh token operations
                localStorage.setItem('lastUsername', usernameToUse);
                // Return tokens in the same format as backend response for compatibility
                return {
                    success: true,
                    tokens: {
                        AuthenticationResult: {
                            AccessToken: result.tokens.AccessToken,
                            IdToken: result.tokens.IdToken,
                            RefreshToken: result.tokens.RefreshToken
                        }
                    }
                };
            }
            else {
                // Authentication failed
                return {
                    success: false,
                    error: result.error || 'Authentication failed',
                    errorCode: result.errorCode, // Pass through error code for better error handling
                    needsTemporaryPassword: result.needsTemporaryPassword
                };
            }
        }
        catch (error) {
            console.error('❌ [BackendClient] SRP authentication error:', error);
            return {
                success: false,
                error: error.message || 'Login failed',
                errorCode: error.code || error.name || undefined
            };
        }
    });
}
/**
 * Login user with temporary password (backend flow)
 *
 * This function is used ONLY for users with temporary passwords (first-time login).
 * It uses ADMIN_USER_PASSWORD_AUTH via the backend which:
 * - Requires AWS credentials (secure for server-side operations)
 * - Supports temporary passwords and NEW_PASSWORD_REQUIRED challenge
 * - Is designed for server-side authentication operations
 *
 * For normal logins (permanent passwords), use loginUser() instead.
 *
 * @param username - User's username
 * @param password - User's temporary password
 * @returns Promise with authentication result containing tokens or challenge information
 */
export function loginUserWithTemporaryPassword(username, password) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Use backend authentication for temporary passwords
            const response = yield fetch(`${BACKEND_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username, password })
            });
            const data = yield response.json();
            // Store username for refresh token operations
            if (data.success) {
                localStorage.setItem('lastUsername', username);
            }
            return data;
        }
        catch (error) {
            console.error('❌ [BackendClient] Temporary password login error:', error);
            return {
                success: false,
                error: error.message || 'Login failed'
            };
        }
    });
}
/**
 * Get public user information by username
 * Used to pre-fill "Complete Setup" form with existing user data from Cognito
 */
export function getUserPublicInfo(username) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(`${BACKEND_URL}/auth/user-info/${encodeURIComponent(username)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            const data = yield response.json();
            return data;
        }
        catch (error) {
            console.error('❌ Error getting user public info:', error);
            return { success: false, error: error.message || 'Failed to get user information' };
        }
    });
}
/**
 * Get current user information
 */
export function getCurrentUser() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const token = localStorage.getItem('accessToken');
            if (!token) {
                return { success: false, error: 'No access token' };
            }
            const response = yield fetch(`${BACKEND_URL}/auth/me`, {
                method: 'GET',
                headers: getAuthHeaders()
            });
            const data = yield response.json();
            return data;
        }
        catch (error) {
            return { success: false, error: error.message || 'Failed to get user info' };
        }
    });
}
/**
 * Logout user
 */
export function logoutUser() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Clear local storage
            localStorage.removeItem('accessToken');
            localStorage.removeItem('idToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('lastUsername');
            return { success: true };
        }
        catch (error) {
            return { success: false, error: error.message || 'Logout failed' };
        }
    });
}
/**
 * Change password for user with temporary password
 */
export function changePassword(newPassword, session) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(`${BACKEND_URL}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ newPassword, session })
            });
            const data = yield response.json();
            return data;
        }
        catch (error) {
            return { success: false, error: error.message || 'Failed to change password' };
        }
    });
}
/**
 * Change password with user attributes for account setup
 */
export function changePasswordWithAttributes(newPassword, session, attributes, username) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(`${BACKEND_URL}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    newPassword,
                    session,
                    attributes,
                    username
                })
            });
            const data = yield response.json();
            return data;
        }
        catch (error) {
            return { success: false, error: error.message || 'Failed to change password with attributes' };
        }
    });
}
/**
 * Initiate password reset process
 * Sends a reset code to the user's email
 */
export function forgotPassword(username) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(`${BACKEND_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username })
            });
            const data = yield response.json();
            return data;
        }
        catch (error) {
            console.error('❌ Error initiating password reset:', error);
            return { success: false, error: error.message || 'Failed to initiate password reset' };
        }
    });
}
/**
 * Confirm password reset with verification code
 * Completes the password reset process
 */
export function confirmForgotPassword(username, confirmationCode, newPassword) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(`${BACKEND_URL}/auth/confirm-forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    username,
                    confirmationCode,
                    newPassword
                })
            });
            const data = yield response.json();
            return data;
        }
        catch (error) {
            console.error('❌ Error confirming password reset:', error);
            return { success: false, error: error.message || 'Failed to confirm password reset' };
        }
    });
}
/**
 * Builds HTTP headers with authentication token for API requests
 * Returns headers with Bearer token if available, otherwise just Content-Type
 */
export function getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    const headers = {
        'Content-Type': 'application/json'
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
}
/**
 * Validates token before making requests by calling /auth/me endpoint
 * Attempts automatic token refresh if token is expired
 * Returns true if token is valid, false otherwise
 */
export function validateTokenBeforeAction() {
    return __awaiter(this, void 0, void 0, function* () {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            console.warn('🔒 No token found, forcing logout');
            forceLogout();
            return false;
        }
        try {
            const response = yield fetch(`${BACKEND_URL}/auth/me`, {
                method: 'GET',
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                console.warn('🔒 Token validation failed, attempting refresh');
                const refreshSuccess = yield refreshAccessToken();
                if (!refreshSuccess) {
                    console.warn('🔒 Token refresh failed, forcing logout');
                    forceLogout();
                    return false;
                }
            }
            return true;
        }
        catch (error) {
            console.error('🔒 Token validation error:', error);
            // Don't logout on network errors, just return false
            return false;
        }
    });
}
/**
 * Show token refresh notification to user
 */
function showTokenRefreshNotification() {
    // Simple console notification for now
    console.log('🔄 Session extended automatically');
    // TODO: Add UI notification if needed
    // You could show a small toast notification here
}
/**
 * Refresh access token using refresh token
 * This function attempts to get a new access token when the current one expires
 * @returns Promise<boolean> - true if refresh successful, false otherwise
 */
function refreshAccessToken() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            const username = localStorage.getItem('lastUsername'); // Get stored username
            // Check if refresh token exists
            if (!refreshToken) {
                console.warn('🔒 No refresh token available - forcing logout');
                forceLogout();
                return false;
            }
            console.log('🔄 Attempting to refresh access token...');
            // Call backend refresh endpoint with username for SECRET_HASH calculation
            const response = yield fetch(`${BACKEND_URL}/auth/refresh-token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    refreshToken,
                    username: username || undefined // Include username if available
                })
            });
            if (response.ok) {
                // Parse new tokens from response
                const data = yield response.json();
                if (data.success && data.accessToken) {
                    localStorage.setItem('accessToken', data.accessToken);
                    // Update refresh token if a new one is provided
                    if (data.refreshToken) {
                        localStorage.setItem('refreshToken', data.refreshToken);
                    }
                    console.log('✅ Token refreshed successfully');
                    // Show user notification that session was extended
                    showTokenRefreshNotification();
                    return true;
                }
                else {
                    console.warn('🔒 Token refresh failed - invalid response format');
                    forceLogout();
                    return false;
                }
            }
            else {
                const errorData = yield response.json().catch(() => ({}));
                console.warn('🔒 Token refresh failed:', response.status, errorData.error || 'Unknown error');
                // If refresh token is invalid or expired, force logout
                if (response.status === 401 || response.status === 403) {
                    console.warn('🔒 Refresh token expired or invalid - forcing logout');
                    forceLogout();
                }
                return false;
            }
        }
        catch (error) {
            console.error('🔒 Error refreshing token:', error);
            // Don't force logout on network errors, just return false
            return false;
        }
    });
}
/**
 * Handles 401 authentication errors by automatically refreshing the token and retrying the request
 * This prevents token expiration errors from breaking the user experience
 *
 * Pattern: All authenticated API functions should use this to handle token expiration gracefully
 *
 * @param response - The failed HTTP response (should be 401)
 * @param retryFunction - Function to retry the original request after token refresh
 * @returns The response after retry, or a 401 response if refresh failed
 */
function handleAuthError(response, retryFunction) {
    return __awaiter(this, void 0, void 0, function* () {
        if (response.status === 429) {
            return response;
        }
        if (response.status === 401) {
            console.warn('🔒 Authentication failed - attempting token refresh');
            // Try to refresh the token automatically
            const refreshSuccess = yield refreshAccessToken();
            if (refreshSuccess && retryFunction) {
                // Retry the original request with the new token
                console.log('✅ Retrying request with refreshed token');
                return retryFunction();
            }
            else {
                // Refresh failed or no retry function - force logout
                console.warn('❌ Token refresh failed - logging out user');
                forceLogout();
                // Return a response that indicates failure
                return new Response(JSON.stringify({ error: 'Authentication failed' }), {
                    status: 401,
                    headers: { 'Content-Type': 'application/json' }
                });
            }
        }
        return response;
    });
}
/**
 * Force logout and redirect to login
 */
function forceLogout() {
    // Clear all stored data
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('lastUsername');
    // Show auth forms and hide main app
    const authContainer = document.getElementById('auth-container');
    const mainApp = document.getElementById('main-layout');
    if (authContainer)
        authContainer.style.display = 'flex';
    if (mainApp)
        mainApp.style.display = 'none';
    // Show login form
    const loginContainer = document.getElementById('login-container');
    const registerContainer = document.getElementById('register-container');
    const confirmContainer = document.getElementById('confirm-email-container');
    if (loginContainer)
        loginContainer.style.display = 'block';
    if (registerContainer)
        registerContainer.style.display = 'none';
    if (confirmContainer)
        confirmContainer.style.display = 'none';
    // Clear any error messages
    const errorElements = document.querySelectorAll('.error-message, .success-message');
    errorElements.forEach(el => el.textContent = '');
    console.log('🔒 User logged out due to token expiration');
}
// ───────────────────────────────────────────────
// Receptor Preparation
// ───────────────────────────────────────────────
/**
 * Launches receptor preparation for the given PDB ID
 */
export function prepareReceptor(pdbID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            // Define the request function for potential retry
            const makeRequest = () => __awaiter(this, void 0, void 0, function* () {
                return fetch(`${BACKEND_URL}/frontend/prepare-receptor`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ pdbID })
                });
            });
            // Make initial request
            let res = yield makeRequest();
            // Handle auth errors with automatic retry
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                // If still not ok after retry, return error
                if (!res.ok) {
                    console.error('Error preparing receptor:', res.status);
                    return { submitted: false };
                }
            }
            const data = yield res.json();
            const submitted = ((_b = (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b.receptorPreparationSubmitted) === true;
            const receptorID = (_d = (_c = data.output_data) === null || _c === void 0 ? void 0 : _c.attributes) === null || _d === void 0 ? void 0 : _d.receptorID;
            return {
                submitted,
                receptorID
            };
        }
        catch (err) {
            console.error('Error preparing receptor:', err);
            return { submitted: false };
        }
    });
}
/**
 * Checks the receptor preparation status
 */
export function checkReceptor(receptorID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        // Validate token before making request
        const tokenValid = yield validateTokenBeforeAction();
        if (!tokenValid) {
            return { status: 'job_submission_failed' };
        }
        // Define the request function for potential retry
        const makeRequest = () => __awaiter(this, void 0, void 0, function* () {
            return fetch(`${BACKEND_URL}/frontend/check-receptor`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ receptorID })
            });
        });
        // Make initial request
        let res = yield makeRequest();
        // Handle auth errors with automatic retry
        if (!res.ok) {
            res = yield handleAuthError(res, makeRequest);
            // If still not ok after retry, return error status
            if (!res.ok) {
                return { status: 'job_submission_failed' };
            }
        }
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
/**
 * Gets all receptors for the current session
 * Uses handleAuthError pattern: automatically refreshes token on 401 and retries request
 */
export function getSessionReceptors() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/get-session-receptors`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({})
            });
        };
        let res = yield makeRequest();
        if (!res.ok)
            res = yield handleAuthError(res, makeRequest);
        if (!res.ok) {
            if (res.status === 429) {
                return { receptors: [], error: 'rate_exceeded' };
            }
            throw new Error('Failed to get session receptors');
        }
        const data = yield res.json();
        return ((_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) || { receptors: [] };
    });
}
/**
 * Deletes a receptor from the session
 */
export function deleteSessionReceptor(receptorID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            const makeRequest = () => {
                return fetch(`${BACKEND_URL}/frontend/delete-session-receptor`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ receptorID })
                });
            };
            let res = yield makeRequest();
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    console.error('[deleteSessionReceptor] ❌ HTTP Error:', res.status);
                    return { deleted: false, reason: 'http_error', message: `HTTP ${res.status}` };
                }
            }
            const data = yield res.json();
            const attrs = ((_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) || {};
            return { deleted: attrs.deleted === true, reason: attrs.reason, message: attrs.message };
        }
        catch (err) {
            console.error('[deleteSessionReceptor] ❌ Exception:', err);
            return { deleted: false, reason: 'exception', message: 'Network or server error' };
        }
    });
}
// ───────────────────────────────────────────────
// Peptide Generation (CPBuilder)
// ───────────────────────────────────────────────
/**
 * Launches peptide preparation (CPBuilder) job
 */
export function preparePeptide(peptideSequence) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            const requestBody = {
                peptideSequence
            };
            const makeRequest = () => {
                return fetch(`${BACKEND_URL}/frontend/prepare-peptide`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(requestBody)
                });
            };
            let res = yield makeRequest();
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    const errorText = yield res.text();
                    console.error('Error preparing peptide:', res.status, errorText);
                    return { submitted: false };
                }
            }
            const data = yield res.json();
            return {
                submitted: ((_b = (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b.peptideGenerationSubmitted) === true,
                peptideID: (_d = (_c = data.output_data) === null || _c === void 0 ? void 0 : _c.attributes) === null || _d === void 0 ? void 0 : _d.peptideID
            };
        }
        catch (err) {
            console.error('Error preparing peptide:', err);
            return { submitted: false };
        }
    });
}
/**
 * Checks status and descriptors of peptide generation
 */
export function checkPeptide(peptideID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/check-peptide`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    peptideID
                })
            });
        };
        let res = yield makeRequest();
        if (!res.ok) {
            res = yield handleAuthError(res, makeRequest);
            if (!res.ok) {
                throw new Error('[checkPeptide] ❌ HTTP Error');
            }
        }
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
/**
 * Gets all peptides for the current session
 */
export function getSessionPeptides() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/get-session-peptides`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({})
            });
        };
        let res = yield makeRequest();
        if (!res.ok)
            res = yield handleAuthError(res, makeRequest);
        if (!res.ok) {
            if (res.status === 429)
                return { peptides: [], error: 'rate_exceeded' };
            throw new Error('[getSessionPeptides] ❌ HTTP Error');
        }
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
/**
 * Deletes a peptide from the session
 */
export function deleteSessionPeptide(peptideID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            const makeRequest = () => {
                return fetch(`${BACKEND_URL}/frontend/delete-session-peptide`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        peptideID
                    })
                });
            };
            let res = yield makeRequest();
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    console.error('[deleteSessionPeptide] ❌ HTTP Error:', res.status);
                    return { deleted: false, reason: 'http_error', message: `HTTP ${res.status}` };
                }
            }
            const data = yield res.json();
            const attrs = ((_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) || {};
            return { deleted: attrs.deleted === true, reason: attrs.reason, message: attrs.message };
        }
        catch (err) {
            console.error('[deleteSessionPeptide] ❌ Exception:', err);
            return { deleted: false, reason: 'exception', message: 'Network or server error' };
        }
    });
}
// ───────────────────────────────────────────────
// Binding Site Selection
// ───────────────────────────────────────────────
/**
 * Gets binding site information for a receptor
 */
export function getBindingSiteInfo(receptorID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/check-binding-site`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    receptorID
                })
            });
        };
        let res = yield makeRequest();
        if (!res.ok) {
            res = yield handleAuthError(res, makeRequest);
            if (!res.ok) {
                throw new Error('[getBindingSiteInfo] ❌ HTTP Error');
            }
        }
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
// ───────────────────────────────────────────────
// Docking
// ───────────────────────────────────────────────
/**
 * Launches the molecular docking process (predefined or custom binding site)
 * @param bindingSiteID - Binding site ID (positive number for predefined, -1 for custom)
 * @param receptorID - Receptor ID
 * @param peptideID - Peptide ID
 * @param customBindingSite - Custom binding site ID (required if bindingSiteID is -1)
 */
export function launchDocking(bindingSiteID, receptorID, peptideID, customBindingSite) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        try {
            const makeRequest = () => {
                const payload = {
                    selectedBindingSite: bindingSiteID,
                    receptorID,
                    peptideID
                };
                // Add custom binding site if present (required when bindingSiteID is -1)
                if (bindingSiteID === -1) {
                    if (!customBindingSite) {
                        console.error('[launchDocking] ERROR: bindingSiteID is -1 but customBindingSite is missing!');
                        throw new Error('customBindingSite is required when bindingSiteID is -1');
                    }
                    payload.customBindingSite = customBindingSite;
                }
                else if (customBindingSite) {
                    // Optional for predefined binding sites
                    payload.customBindingSite = customBindingSite;
                }
                console.log(`[launchDocking] Payload:`, payload);
                return fetch(`${BACKEND_URL}/frontend/dock`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload)
                });
            };
            let res = yield makeRequest();
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    console.error('[launchDocking] ❌ HTTP Error:', res.status);
                    return false;
                }
            }
            const data = yield res.json();
            return ((_b = (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b.dockingSubmitted) === true;
        }
        catch (err) {
            console.error('[launchDocking] ❌ Exception:', err);
            return false;
        }
    });
}
/**
 * Checks if docking results are available
 */
export function checkDocking(receptorID, peptideID, selectedBindingSite, customBindingSite) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            const payload = {
                receptorID,
                peptideID,
                selectedBindingSite
            };
            // Add custom binding site if present (required when selectedBindingSite is -1)
            if (selectedBindingSite === -1) {
                if (!customBindingSite) {
                    console.error('[checkDocking] ERROR: selectedBindingSite is -1 but customBindingSite is missing!');
                    throw new Error('customBindingSite is required when selectedBindingSite is -1');
                }
                payload.customBindingSite = customBindingSite;
            }
            else if (customBindingSite) {
                // Optional for predefined binding sites
                payload.customBindingSite = customBindingSite;
            }
            console.log(`[checkDocking] Payload:`, payload);
            return fetch(`${BACKEND_URL}/frontend/check-dock`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
        };
        let res = yield makeRequest();
        if (!res.ok) {
            res = yield handleAuthError(res, makeRequest);
            if (!res.ok) {
                throw new Error('[checkDocking] ❌ HTTP Error');
            }
        }
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
/**
 * Gets all dockings for the current session
 */
export function getSessionDockings() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/get-session-dockings`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({})
            });
        };
        let res = yield makeRequest();
        if (!res.ok)
            res = yield handleAuthError(res, makeRequest);
        if (!res.ok) {
            if (res.status === 429)
                return { dockings: [], error: 'rate_exceeded' };
            throw new Error('[getSessionDockings] ❌ HTTP Error');
        }
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
/**
 * Deletes a docking from the current session
 */
export function deleteSessionDocking(dockingID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        try {
            const makeRequest = () => {
                return fetch(`${BACKEND_URL}/frontend/delete-session-docking`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ dockingID })
                });
            };
            let res = yield makeRequest();
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    console.error('[deleteSessionDocking] ❌ HTTP Error:', res.status);
                    return false;
                }
            }
            const data = yield res.json();
            return ((_b = (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b.deleted) === true;
        }
        catch (err) {
            console.error('[deleteSessionDocking] ❌ Exception:', err);
            return false;
        }
    });
}
// ───────────────────────────────────────────────
// Screenings
// ───────────────────────────────────────────────
export function getSessionScreenings(receptorID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/get-session-screenings`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ receptorID })
            });
        };
        let res = yield makeRequest();
        if (!res.ok)
            res = yield handleAuthError(res, makeRequest);
        if (!res.ok) {
            if (res.status === 429)
                return { screenings: [], error: 'rate_exceeded' };
            throw new Error('[getSessionScreenings] ❌ HTTP Error');
        }
        const data = yield res.json();
        return ((_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) || { screenings: [] };
    });
}
export function launchScreening(receptorID, selectedBindingSite, customBindingSite) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/launch-screening`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(Object.assign({ receptorID,
                    selectedBindingSite }, (customBindingSite && { customBindingSite })))
            });
        };
        let res = yield makeRequest();
        if (!res.ok)
            res = yield handleAuthError(res, makeRequest);
        const data = yield res.json();
        if (!data.successful)
            return { screeningSubmitted: false, error: (_b = (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b.error };
        return { screeningSubmitted: ((_d = (_c = data.output_data) === null || _c === void 0 ? void 0 : _c.attributes) === null || _d === void 0 ? void 0 : _d.screeningSubmitted) === true };
    });
}
export function checkScreening(screeningID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/check-screening`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ screeningID })
            });
        };
        let res = yield makeRequest();
        if (!res.ok)
            res = yield handleAuthError(res, makeRequest);
        if (!res.ok)
            throw new Error('[checkScreening] ❌ HTTP Error');
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
export function stopScreening(screeningID) {
    return __awaiter(this, void 0, void 0, function* () {
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/stop-screening`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ screeningID })
            });
        };
        let res = yield makeRequest();
        if (!res.ok)
            res = yield handleAuthError(res, makeRequest);
        const data = yield res.json();
        return { success: data.success === true, message: data.message };
    });
}
export function deleteSessionScreening(screeningID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/delete-session-screening`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ screeningID })
            });
        };
        let res = yield makeRequest();
        if (!res.ok)
            res = yield handleAuthError(res, makeRequest);
        if (!res.ok)
            return false;
        const data = yield res.json();
        return ((_b = (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b.deleted) === true;
    });
}
// ───────────────────────────────────────────────
// Custom Binding Sites
// ───────────────────────────────────────────────
/**
 * Gets all custom binding sites for a receptor
 */
export function getCustomBindingSites(receptorID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            const url = `${BACKEND_URL}/frontend/get-session-custom-binding-sites`;
            const payload = { receptorID };
            console.log('[getCustomBindingSites] Request URL:', url);
            console.log('[getCustomBindingSites] Request payload:', payload);
            return fetch(url, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
        };
        let res = yield makeRequest();
        if (!res.ok) {
            console.error('[getCustomBindingSites] First request failed:', res.status, res.statusText);
            res = yield handleAuthError(res, makeRequest);
            if (!res.ok) {
                const errorText = yield res.text();
                console.error('[getCustomBindingSites] Retry failed:', res.status, errorText);
                throw new Error(`[getCustomBindingSites] ❌ HTTP Error ${res.status}: ${errorText}`);
            }
        }
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
/**
 * Creates a new custom binding site
 */
export function createCustomBindingSite(receptorID, centroid, residues) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const makeRequest = () => {
            return fetch(`${BACKEND_URL}/frontend/create-session-custom-binding-site`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ receptorID, centroid, residues })
            });
        };
        let res = yield makeRequest();
        if (!res.ok) {
            res = yield handleAuthError(res, makeRequest);
            if (!res.ok) {
                const errorText = yield res.text();
                try {
                    const errorData = JSON.parse(errorText);
                    throw new Error(errorData.error || '[createCustomBindingSite] ❌ HTTP Error');
                }
                catch (_b) {
                    throw new Error('[createCustomBindingSite] ❌ HTTP Error');
                }
            }
        }
        const data = yield res.json();
        return (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes;
    });
}
/**
 * Deletes a custom binding site
 */
export function deleteCustomBindingSite(receptorID, bindingSiteId) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        try {
            const makeRequest = () => {
                return fetch(`${BACKEND_URL}/frontend/delete-session-custom-binding-site`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ receptorID, bindingSiteId })
                });
            };
            let res = yield makeRequest();
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    console.error('[deleteCustomBindingSite] ❌ HTTP Error:', res.status);
                    return false;
                }
            }
            const data = yield res.json();
            return ((_b = (_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b.deleted) === true;
        }
        catch (err) {
            console.error('[deleteCustomBindingSite] ❌ Exception:', err);
            return false;
        }
    });
}
// ───────────────────────────────────────────────
// Stop Job Functions
// ───────────────────────────────────────────────
/**
 * Stops a running receptor preparation job
 *
 * @param receptorID - The receptor ID to stop
 * @returns Promise with success status and message
 */
export function stopReceptor(receptorID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        console.log('[stopReceptor API] Calling backend with receptorID:', receptorID);
        try {
            const makeRequest = () => {
                return fetch(`${BACKEND_URL}/frontend/stop-receptor`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ receptorID })
                });
            };
            let res = yield makeRequest();
            console.log('[stopReceptor API] Response status:', res.status);
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    console.error('[stopReceptor] ❌ HTTP Error:', res.status);
                    const errorText = yield res.text();
                    console.error('[stopReceptor] ❌ Error response:', errorText);
                    return { success: false, message: `HTTP ${res.status}` };
                }
            }
            const data = yield res.json();
            console.log('[stopReceptor API] Response data:', data);
            return ((_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) || { success: false };
        }
        catch (err) {
            console.error('[stopReceptor] ❌ Exception:', err);
            return { success: false, message: 'Network or server error' };
        }
    });
}
/**
 * Stops a running peptide preparation job
 *
 * @param peptideID - The peptide ID to stop
 * @returns Promise with success status and message
 */
export function stopPeptide(peptideID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            const makeRequest = () => {
                return fetch(`${BACKEND_URL}/frontend/stop-peptide`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ peptideID })
                });
            };
            let res = yield makeRequest();
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    console.error('[stopPeptide] ❌ HTTP Error:', res.status);
                    return { success: false, message: `HTTP ${res.status}` };
                }
            }
            const data = yield res.json();
            return ((_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) || { success: false };
        }
        catch (err) {
            console.error('[stopPeptide] ❌ Exception:', err);
            return { success: false, message: 'Network or server error' };
        }
    });
}
/**
 * Stops a running docking job
 *
 * @param dockingID - The docking ID to stop (format: "receptorID-peptideID-bbindingSiteID")
 * @returns Promise with success status and message
 */
export function stopDocking(dockingID) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            const makeRequest = () => {
                return fetch(`${BACKEND_URL}/frontend/stop-docking`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ dockingID })
                });
            };
            let res = yield makeRequest();
            if (!res.ok) {
                res = yield handleAuthError(res, makeRequest);
                if (!res.ok) {
                    console.error('[stopDocking] ❌ HTTP Error:', res.status);
                    return { success: false, message: `HTTP ${res.status}` };
                }
            }
            const data = yield res.json();
            return ((_a = data.output_data) === null || _a === void 0 ? void 0 : _a.attributes) || { success: false };
        }
        catch (err) {
            console.error('[stopDocking] ❌ Exception:', err);
            return { success: false, message: 'Network or server error' };
        }
    });
}
